import React, {useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {FileUpload} from 'primereact/fileupload';
import {Editor} from 'primereact/editor';
import appStore from '../storage/AppStore';
import newStore from '../storage/NewStore';
import systemStore from '../storage/SystemStore';
import PageLayout from './PageLayout';
import Input from '../ui/Input';
import Phone from '../ui/Phone';
import CountrySelect from '../ui/CountrySelect';
import Btn from '../ui/Btn';
import Location from '../ui/Location';
import MultipleSelect from '../ui/MultipleSelect';
import {SAFE_FORMATS} from '../common/utils';
import {getProduct} from '../common/config';
import '../common/company_registration.css';

const CompanyEdit = ({company, role = 'admin'}) => {
  const isClient = role === 'client';
  const isCourse = getProduct() === 'course1';
  const tr = appStore.tr;
  const ownerPhone = appStore.user?.phone_number;

  useEffect(() => {
    if (!company) return;
    if (!company.country_id && appStore.user?.country_id) company.setProperty('country_id', appStore.user.country_id);
    if (!company.locations?.length) company.addLocation?.();
    if (isClient && ownerPhone && company.owner_phone !== ownerPhone) company.setProperty('owner_phone', ownerPhone);
  }, [company, company?.country_id, company?.locations?.length, appStore.user?.country_id, ownerPhone, isClient]);

  if (!company) return null;
  const aboutInvalid = Boolean(company.was_validated && !company.about);

  const handleSave = async () => {
    try {
      if (isClient) {
        if (!ownerPhone) {systemStore.setError(tr.company_registration_phone_title); return;}
        company.setProperty('owner_phone', ownerPhone);
      }
      (company.locations || []).forEach(location => location.validate?.());
      if (company.validate?.() === false) {systemStore.setError(tr.please_check_data); return;}
      await company.saveCompany();
      systemStore.setSuccess(tr.successfully_saved);
      if (role) await newStore.checkPendingCompanies();
    } catch (error) {
      systemStore.setError(tr[error?.message] || error?.message || 'Error');
    }
  };

  return (
    <PageLayout header={<h3 className="m-0">{company.id ? tr.edit : tr.add_company}</h3>}>
      <div className="cr-page company-registration">
        <div className="cr-heading">
          <div className="cr-heading-icon"><i className="pi pi-building" aria-hidden="true"/></div>
          <div><h2>{company.id ? tr.edit : tr.add_company}</h2><p>{company.id ? tr.edit : tr.company_registration_intro}</p></div>
        </div>

        <section className="cr-card" aria-labelledby="cr-basic-title">
          <div className="cr-section-title"><i className="pi pi-id-card" aria-hidden="true"/><h3 id="cr-basic-title">{tr.company_registration_basic}</h3></div>
          <div className="cr-logo-field">
            <div className="cr-logo-preview">
              {company.logo ? <img src={company.logo} alt={company.name || tr.logo}/> : <i className="pi pi-image" aria-hidden="true"/>}
            </div>
            <div className="cr-logo-controls"><span className="cr-label">{tr.logo}</span>
              <FileUpload mode="basic" name="input_photo[]" accept="image/*" chooseLabel={tr.logo}
                disabled={systemStore.loading} onSelect={e => {if (e.files?.[0]) company.setProperty('photo_upload', e.files[0]);}}/>
            </div>
          </div>
          <div className="cr-form-grid">
            <div className="cr-field cr-span-full"><label className="cr-label" htmlFor="company-name">{tr.company_name}</label>
              <Input id="company-name" store={company} prop="name" label={tr.club_name || tr.company_name} required/>
            </div>
            <div className="cr-field cr-span-full"><label className="cr-label">{tr.about_company}</label>
              <Editor value={company.about || ''} onTextChange={e => company.setProperty('about', e.htmlValue || '')}
                readOnly={systemStore.loading} formats={SAFE_FORMATS} className={aboutInvalid ? 'p-invalid' : ''} style={{height: '250px'}}/>
              {aboutInvalid && <small className="p-error">{tr.please_check_data}</small>}
            </div>
          </div>
        </section>

        <section className="cr-card" aria-labelledby="cr-contact-title">
          <div className="cr-section-title"><i className="pi pi-phone" aria-hidden="true"/><h3 id="cr-contact-title">{tr.company_registration_contacts}</h3></div>
          <div className="cr-form-grid">
            <div className="cr-field"><label className="cr-label" htmlFor="input_owner_phone">{tr.owner}</label>
              {isClient ? <div className="cr-readonly-phone"><i className="pi pi-check-circle" aria-hidden="true"/><span>{ownerPhone}</span></div> :
                <Phone id="input_owner_phone" country={company.country_id?.toLowerCase() || 'kg'} store={company} prop="owner_phone" required/>}
            </div>
            <div className="cr-field"><label className="cr-label" htmlFor="input_phone_number">{tr.phone_number}</label>
              <Phone id="input_phone_number" country={company.country_id?.toLowerCase() || 'kg'} store={company} prop="phone_number" required/>
            </div>
            <div className="cr-field"><label className="cr-label" htmlFor="telegram">Telegram</label><Input id="telegram" store={company} prop="telegram" label="Telegram"/></div>
            <div className="cr-field"><label className="cr-label" htmlFor="whatsapp">WhatsApp</label><Input id="whatsapp" store={company} prop="whatsapp" label="WhatsApp"/></div>
            <div className="cr-field"><label className="cr-label" htmlFor="instagram">Instagram</label><Input id="instagram" store={company} prop="instagram" label="Instagram"/></div>
            <div className="cr-field"><label className="cr-label" htmlFor="website">Website</label><Input id="website" store={company} prop="website" label="Website"/></div>
          </div>
        </section>

        <section className="cr-card" aria-labelledby="cr-place-title">
          <div className="cr-section-title"><i className="pi pi-map-marker" aria-hidden="true"/><h3 id="cr-place-title">{tr.company_registration_location}</h3></div>
          <div className="cr-form-grid">
            <div className="cr-field cr-span-full"><CountrySelect store={company}/></div>
            <div className="cr-field cr-span-full"><label className="cr-label">{isCourse ? tr.course_types : tr.sport_types}</label>
              <MultipleSelect inputId="company-types" store={company} prop="sports" options={appStore.sports || []}
                placeholder={isCourse ? tr.course_types : tr.sport_types} required/>
            </div>
          </div>
        </section>

        <section className="cr-card" aria-labelledby="cr-locations-title">
          <div className="cr-section-title cr-section-title-split"><div className="cr-section-title"><i className="pi pi-map" aria-hidden="true"/><h3 id="cr-locations-title">{tr.locations}</h3></div>
            <Btn icon="pi pi-plus" label={tr.add} outlined onClick={() => company.addLocation?.()}/>
          </div>
          <div className="cr-locations">
            {(company.locations || []).map((location, index) => <div className="cr-location-item" key={location.id ?? index}>
              <Location store={company} location={location}/>
            </div>)}
          </div>
        </section>

        <div className="cr-save-bar"><Btn icon="pi pi-save" severity="success" className="cr-save-button"
          disabled={systemStore.loading} label={company.id ? (tr.save || tr.edit) : tr.add} onClick={handleSave}/></div>
      </div>
    </PageLayout>
  );
};

export default observer(CompanyEdit);
